import React, { useState } from 'react';
import { MindfulnessReport, Recording } from '../types';
import { GuidedBreathing } from './GuidedBreathing';

interface MindfulnessModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: MindfulnessReport | null;
  recording: Recording | null;
  isLoading?: boolean;
  onOpenFeedback?: () => void;
}

export const MindfulnessModal: React.FC<MindfulnessModalProps> = ({
  isOpen,
  onClose,
  report,
  recording,
  isLoading = false,
  onOpenFeedback,
}) => {
  const [copied, setCopied] = useState(false);
  const [showDetailedInsights, setShowDetailedInsights] = useState(false);

  if (!isOpen) return null;

  const handleCopySummary = () => {
    if (!report || !recording) return;
    const paceLabel = report.breathingPattern
      ? `${report.breathingPattern.name} • ${report.breathingPattern.badge}`
      : report.recommendedBreathingPace;

    const text = `🧘 Kinetic Harmony - Mindfulness & Flow Report
Session: ${recording.name} (${report.durationCategory} • ${recording.metrics?.durationSeconds?.toFixed(0) || ''}s)
Soundscape: ${recording.metrics?.presetName || 'Ambient'}
Flow State: ${report.flowStateScore}/100 • Relaxation: ${report.relaxationScore}/100 (${report.relaxationLevel})
${report.summary ? `\nOverview: ${report.summary}` : ''}

Key Highlights:
• ${report.mindfulnessHighlights.join('\n• ')}

Mind & Body Connection:
${report.somaticReflection}

Personalized Breathing:
${paceLabel}`;

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      id="mindfulness-modal-overlay"
      className="fixed inset-0 z-[70] flex items-center justify-center p-2.5 sm:p-6 bg-slate-950/80 backdrop-blur-md overflow-y-auto overflow-x-hidden animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
    >
      <div
        id="mindfulness-report-card"
        className="bg-slate-900/95 border border-white/15 rounded-2xl shadow-2xl max-w-2xl w-full p-4 sm:p-8 text-white relative overflow-hidden my-auto max-h-[92vh] sm:max-h-[90vh] flex flex-col"
      >
        {/* Background glow accents */}
        <div className="absolute -top-24 -right-24 w-72 h-72 bg-indigo-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />

        {/* Modal Header */}
        <div className="flex items-start justify-between pb-3 sm:pb-4 border-b border-white/10 shrink-0 relative z-10 gap-2 min-w-0">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-teal-400 p-0.5 flex items-center justify-center shadow-lg shadow-indigo-500/20 shrink-0">
              <div className="w-full h-full bg-slate-900 rounded-[10px] flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 sm:w-5 sm:h-5 text-teal-300">
                  <path fillRule="evenodd" d="M9 4.5a.75.75 0 01.721.544l.813 2.846a3.75 3.75 0 002.576 2.576l2.846.813a.75.75 0 010 1.442l-2.846.813a3.75 3.75 0 00-2.576 2.576l-.813 2.846a.75.75 0 01-1.442 0l-.813-2.846a3.75 3.75 0 00-2.576-2.576l-2.846-.813a.75.75 0 010-1.442l2.846-.813A3.75 3.75 0 007.466 7.89l.813-2.846A.75.75 0 019 4.5zM18 1.5a.75.75 0 01.728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 010 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 01-1.456 0l-.258-1.036a2.625 2.625 0 00-1.91-1.91l-1.036-.258a.75.75 0 010-1.456l1.036-.258a2.625 2.625 0 001.91-1.91l.258-1.036A.75.75 0 0118 1.5z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                <h3 className="text-base sm:text-lg font-bold text-white tracking-tight truncate">Mindfulness & Flow</h3>
                {report && (
                  <span className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border shrink-0 ${
                    report.durationCategory === 'Deep Immersion'
                      ? 'bg-purple-500/20 text-purple-300 border-purple-500/30'
                      : report.durationCategory === 'Flow State'
                      ? 'bg-teal-500/20 text-teal-300 border-teal-500/30'
                      : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
                  }`}>
                    {report.durationCategory}
                  </span>
                )}
              </div>
              <p className="text-[11px] sm:text-xs text-slate-400 truncate">
                {recording?.name || 'Performance Session'} • {new Date(recording?.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>

          <button
            id="close-mindfulness-modal-btn"
            onClick={onClose}
            disabled={isLoading}
            className="text-slate-400 hover:text-white p-1 sm:p-1.5 hover:bg-white/10 rounded-lg transition shrink-0"
            aria-label="Close Mindfulness Modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto overflow-x-hidden pr-0.5 sm:pr-1 py-3 sm:py-4 flex-1 custom-scrollbar space-y-3.5 sm:space-y-5 relative z-10">
          {isLoading ? (
            <div className="py-16 text-center space-y-4">
              <div className="relative w-16 h-16 mx-auto">
                <div className="absolute inset-0 rounded-full border-2 border-indigo-500/30 animate-ping"></div>
                <div className="w-16 h-16 rounded-full border-2 border-t-teal-400 border-r-indigo-500 border-b-transparent border-l-transparent animate-spin flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full bg-indigo-950/80 flex items-center justify-center">
                    <span className="w-2.5 h-2.5 rounded-full bg-teal-400 animate-pulse"></span>
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <h4 className="text-sm font-semibold text-slate-200">Tuning In to Your Flow & Rhythm...</h4>
                <p className="text-xs text-slate-400 max-w-sm mx-auto">
                  Analyzing your movement pace, gesture fluidity, and breathing cadence.
                </p>
              </div>
            </div>
          ) : report ? (
            <>
              {/* Optional Audio Bar inside Report */}
              {recording?.url && (
                <div className="bg-slate-950/60 border border-white/10 rounded-xl p-2.5 sm:p-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 min-w-0">
                  <div className="flex items-center gap-2 text-xs font-semibold text-slate-300 shrink-0">
                    <span className="w-2 h-2 rounded-full bg-teal-400"></span>
                    <span>Session Audio</span>
                  </div>
                  <audio src={recording.url} controls className="w-full h-8 max-w-full" />
                </div>
              )}

              {/* Dynamic Analysis Depth Banner */}
              <div className="bg-indigo-950/40 border border-indigo-500/30 rounded-xl p-2.5 sm:p-3.5 flex items-center gap-2.5 sm:gap-3 text-xs text-indigo-200 min-w-0">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-indigo-500/20 text-indigo-300 flex items-center justify-center shrink-0 border border-indigo-500/30">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                  </svg>
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-bold text-white block text-xs sm:text-sm truncate">
                    {report.durationCategory === 'Deep Immersion'
                      ? 'Unhurried Mindful Immersion (> 60s)'
                      : report.durationCategory === 'Flow State'
                      ? 'Harmonic Flow Session (15s – 60s)'
                      : 'Quick Mindful Pause (< 15s)'}
                  </span>
                  <span className="text-[10px] sm:text-[11px] text-indigo-300/80 block leading-tight">
                    {report.durationCategory === 'Deep Immersion'
                      ? 'Sustained peaceful rhythm, deep focus, and unhurried ease.'
                      : report.durationCategory === 'Flow State'
                      ? 'Fluid hand gestures, steady pacing, and calm present focus.'
                      : 'A brief moment of gentle motion to refresh attention and center your breath.'}
                  </span>
                </div>
              </div>

              {/* Top Score Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
                {/* Flow State Score Card */}
                <div className="bg-gradient-to-br from-indigo-950/60 to-slate-950/80 border border-indigo-500/25 rounded-2xl p-3.5 sm:p-4 flex items-center justify-between shadow-lg relative overflow-hidden min-w-0">
                  <div className="relative z-10 min-w-0 flex-1 pr-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse shrink-0"></span>
                      <span className="text-[10px] sm:text-[11px] font-bold tracking-wider uppercase text-indigo-300 truncate">Flow State</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl sm:text-3xl font-black text-white">{report.flowStateScore}</span>
                      <span className="text-xs text-slate-400 font-medium">/100</span>
                    </div>
                    <span className="inline-block mt-1 sm:mt-1.5 text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-200 border border-indigo-500/30 truncate max-w-full">
                      {report.flowStateScore > 85 ? '✨ Deep Creative Flow' : report.flowStateScore > 70 ? '🌊 Harmonic Presence' : '🌱 Mindful Exploring'}
                    </span>
                  </div>
                  <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center shrink-0">
                    <svg className="w-14 h-14 sm:w-16 sm:h-16 transform -rotate-90" viewBox="0 0 36 36">
                      <path
                        className="text-slate-800"
                        strokeWidth="3.5"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className="text-indigo-500 transition-all duration-1000 ease-out"
                        strokeDasharray={`${report.flowStateScore}, 100`}
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="absolute text-[11px] sm:text-xs font-bold text-indigo-300">{report.flowStateScore}%</span>
                  </div>
                </div>

                {/* Relaxation Score Card */}
                <div className="bg-gradient-to-br from-teal-950/60 to-slate-950/80 border border-teal-500/25 rounded-2xl p-3.5 sm:p-4 flex items-center justify-between shadow-lg relative overflow-hidden min-w-0">
                  <div className="relative z-10 min-w-0 flex-1 pr-2">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse shrink-0"></span>
                      <span className="text-[10px] sm:text-[11px] font-bold tracking-wider uppercase text-teal-300 truncate">Relaxation Mood</span>
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl sm:text-3xl font-black text-white">{report.relaxationScore}</span>
                      <span className="text-xs text-slate-400 font-medium">/100</span>
                    </div>
                    <span className="inline-block mt-1 sm:mt-1.5 text-[9px] sm:text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-200 border border-teal-500/30 truncate max-w-full">
                      {report.relaxationLevel}
                    </span>
                  </div>
                  <div className="relative w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center shrink-0">
                    <svg className="w-14 h-14 sm:w-16 sm:h-16 transform -rotate-90" viewBox="0 0 36 36">
                      <path
                        className="text-slate-800"
                        strokeWidth="3.5"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className="text-teal-400 transition-all duration-1000 ease-out"
                        strokeDasharray={`${report.relaxationScore}, 100`}
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        stroke="currentColor"
                        fill="none"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <span className="absolute text-[11px] sm:text-xs font-bold text-teal-300">{report.relaxationScore}%</span>
                  </div>
                </div>
              </div>

              {/* Performance Metrics Bento */}
              {recording?.metrics && (
                <div className="bg-slate-950/60 border border-white/10 rounded-xl sm:rounded-2xl p-3 sm:p-4 min-w-0">
                  <div className="flex items-center justify-between mb-2.5 sm:mb-3 gap-2">
                    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5 shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-teal-400">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .414.336.75.75.75h4a.75.75 0 000-1.5h-3.25V5z" clipRule="evenodd" />
                      </svg>
                      Motion & Cadence
                    </span>
                    <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium truncate">
                      {recording.metrics.totalNotesTriggered || 0} notes played
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-2.5">
                    {/* Metric 1: Duration */}
                    <div className="bg-slate-900/90 rounded-xl p-2.5 sm:p-3 border border-white/5 flex flex-col justify-between min-w-0">
                      <div className="flex items-center justify-between text-slate-400 mb-1">
                        <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider">Duration</span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-indigo-400 shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base font-bold text-white truncate">{recording.metrics.durationSeconds.toFixed(1)}s</span>
                      <span className="text-[9px] sm:text-[10px] text-indigo-300 mt-0.5 sm:mt-1 truncate block">
                        {report.durationCategory}
                      </span>
                    </div>

                    {/* Metric 2: Pacing */}
                    <div className="bg-slate-900/90 rounded-xl p-2.5 sm:p-3 border border-white/5 flex flex-col justify-between min-w-0">
                      <div className="flex items-center justify-between text-slate-400 mb-1">
                        <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider">Pacing</span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400 shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base font-bold text-white truncate">{(recording.metrics.averageIntensity * 100).toFixed(0)}%</span>
                      <span className="text-[9px] sm:text-[10px] text-amber-300 mt-0.5 sm:mt-1 truncate block">
                        {recording.metrics.averageIntensity < 0.3 ? 'Gentle & Calm' : recording.metrics.averageIntensity > 0.6 ? 'High Energy' : 'Moderate Flow'}
                      </span>
                    </div>

                    {/* Metric 3: Tempo Stability */}
                    <div className="bg-slate-900/90 rounded-xl p-2.5 sm:p-3 border border-white/5 flex flex-col justify-between min-w-0">
                      <div className="flex items-center justify-between text-slate-400 mb-1">
                        <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider">Tempo Rhythm</span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-teal-400 shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 107.5 7.5h-7.5V6z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0013.5 3v7.5z" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base font-bold text-teal-300 truncate">{recording.metrics.tempoStability}%</span>
                      <span className="text-[9px] sm:text-[10px] text-teal-400 mt-0.5 sm:mt-1 truncate block">
                        {recording.metrics.tempoStability > 85 ? 'Steady Cadence' : 'Dynamic Timing'}
                      </span>
                    </div>

                    {/* Metric 4: Fluidity */}
                    <div className="bg-slate-900/90 rounded-xl p-2.5 sm:p-3 border border-white/5 flex flex-col justify-between min-w-0">
                      <div className="flex items-center justify-between text-slate-400 mb-1">
                        <span className="text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider">Fluidity</span>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-purple-400 shrink-0">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base font-bold text-purple-300 truncate">{recording.metrics.smoothnessScore}%</span>
                      <span className="text-[9px] sm:text-[10px] text-purple-400 mt-0.5 sm:mt-1 truncate block">
                        {recording.metrics.smoothnessScore > 80 ? 'Seamless Ease' : 'Expressive Shifts'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Visual Session Insights (Session Focus & Collapsible Detailed Highlights) */}
              <div className="space-y-2.5 min-w-0">
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5 shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-indigo-400 shrink-0">
                      <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                    </svg>
                    Session Focus
                  </span>

                  <button
                    id="toggle-detailed-highlights-btn"
                    onClick={() => setShowDetailedInsights(!showDetailedInsights)}
                    className="flex items-center gap-1 sm:gap-1.5 text-[11px] sm:text-xs font-semibold px-2.5 py-1 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 text-indigo-300 hover:text-white border border-indigo-500/20 hover:border-indigo-500/40 transition cursor-pointer shrink-0"
                    title={showDetailedInsights ? 'Hide detailed highlights' : 'Show detailed rhythm, movement, and soundscape insights'}
                  >
                    <span>{showDetailedInsights ? 'Less' : 'More'}</span>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${showDetailedInsights ? 'rotate-180' : ''}`}
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                {/* Session Focus Full Summary Banner - Never truncated on mobile or desktop */}
                {report.summary && (
                  <div className="bg-indigo-950/40 border border-indigo-500/25 rounded-xl px-3 py-2 sm:py-2.5 flex items-start sm:items-center gap-2.5 text-indigo-200">
                    <span className="text-indigo-400 font-bold text-xs sm:text-sm shrink-0 mt-0.5 sm:mt-0">✦</span>
                    <span className="text-xs sm:text-sm font-medium text-indigo-100 leading-snug break-words flex-1">
                      {report.summary}
                    </span>
                  </div>
                )}

                {/* Detailed Highlights: Rhythm Cadence, Movement Fluidity, Acoustic Mood */}
                {showDetailedInsights && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-2.5 pt-1 animate-fade-in min-w-0">
                    {/* Highlight 1: Cadence */}
                    <div className="bg-slate-950/70 border border-indigo-500/20 rounded-xl p-2.5 sm:p-3 flex flex-col justify-between hover:border-indigo-500/40 transition min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-lg bg-indigo-500/20 text-indigo-300 flex items-center justify-center shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <span className="text-[10px] sm:text-[11px] font-bold text-slate-200 truncate">Rhythm Cadence</span>
                      </div>
                      <p className="text-[11px] sm:text-xs text-slate-300 leading-snug">
                        {report.mindfulnessHighlights[0] || 'Steady, reassuring cadence maintaining calm focus.'}
                      </p>
                    </div>

                    {/* Highlight 2: Physical Ease */}
                    <div className="bg-slate-950/70 border border-teal-500/20 rounded-xl p-2.5 sm:p-3 flex flex-col justify-between hover:border-teal-500/40 transition min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-lg bg-teal-500/20 text-teal-300 flex items-center justify-center shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                          </svg>
                        </div>
                        <span className="text-[10px] sm:text-[11px] font-bold text-slate-200 truncate">Movement Fluidity</span>
                      </div>
                      <p className="text-[11px] sm:text-xs text-slate-300 leading-snug">
                        {report.mindfulnessHighlights[1] || 'Smooth, natural gestures fostering physical ease.'}
                      </p>
                    </div>

                    {/* Highlight 3: Soundscape Resonance */}
                    <div className="bg-slate-950/70 border border-purple-500/20 rounded-xl p-2.5 sm:p-3 flex flex-col justify-between hover:border-purple-500/40 transition min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="w-6 h-6 rounded-lg bg-purple-500/20 text-purple-300 flex items-center justify-center shrink-0">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3.5 h-3.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 9l10.5-3m0 6.553v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 11-.99-3.467l2.31-.66a2.25 2.25 0 001.632-2.163zm0 0V2.25L9 5.25v10.303m0 0v3.75a2.25 2.25 0 01-1.632 2.163l-1.32.377a1.803 1.803 0 01-.99-3.467l2.31-.66A2.25 2.25 0 009 15.553z" />
                          </svg>
                        </div>
                        <span className="text-[10px] sm:text-[11px] font-bold text-slate-200 truncate">Acoustic Mood</span>
                      </div>
                      <p className="text-[11px] sm:text-xs text-slate-300 leading-snug">
                        {report.mindfulnessHighlights[2] || 'Resonant harmonic tones creating a peaceful ambiance.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Mind & Body Connection Dynamic Reflection Card */}
              <div className="bg-gradient-to-r from-teal-950/40 via-slate-950/70 to-indigo-950/40 border border-teal-500/25 rounded-xl p-3 sm:p-4 flex items-start gap-2.5 sm:gap-3.5 min-w-0 shadow-md">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-teal-500/20 text-teal-300 border border-teal-500/30 flex items-center justify-center shrink-0 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 sm:w-4 sm:h-4">
                    <path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.758 17.303a.75.75 0 00-1.061-1.06l-1.591 1.59a.75.75 0 001.06 1.061l1.591-1.59zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.697 7.757a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 00-1.061 1.06l1.59 1.591z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-teal-300">
                      Mind & Body Connection
                    </span>
                    <span className="text-[9px] sm:text-[10px] text-teal-400/80 bg-teal-950/60 px-1.5 py-0.5 rounded border border-teal-500/20 font-medium">
                      Session Pattern
                    </span>
                  </div>
                  <p className="text-xs sm:text-[13px] text-slate-200 leading-relaxed font-normal">
                    {report.somaticReflection}
                  </p>
                </div>
              </div>

              {/* Guided Breathing Exercise (Dynamic & Personalized to Session Data) */}
              <GuidedBreathing recommendedPattern={report.breathingPattern} />
            </>
          ) : (
            <div className="text-center py-10 text-slate-400 text-xs">
              No report available for this session.
            </div>
          )}
        </div>

        {/* Modal Footer Actions */}
        <div className="pt-3 sm:pt-4 border-t border-white/10 flex items-center justify-between shrink-0 relative z-10 gap-2 min-w-0">
          <button
            id="copy-summary-btn"
            onClick={handleCopySummary}
            disabled={!report || isLoading}
            className="text-[11px] sm:text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl transition flex items-center gap-1.5 shrink-0 min-h-[36px]"
          >
            {copied ? (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5 text-teal-400 shrink-0">
                  <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                </svg>
                <span className="truncate">Copied</span>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3.5 h-3.5 shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                </svg>
                <span className="truncate">Copy Summary</span>
              </>
            )}
          </button>

          {onOpenFeedback && (
            <button
              id="report-feedback-btn"
              onClick={onOpenFeedback}
              title="Share feedback on your session experience (Google Form)"
              className="text-[11px] sm:text-xs bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-xl transition flex items-center gap-1.5 shrink-0 min-h-[36px] active:scale-95"
            >
              <span>⭐</span>
              <span className="hidden xs:inline sm:inline">Rate Session & App</span>
              <span className="xs:hidden sm:hidden">Feedback</span>
            </button>
          )}

          <button
            id="close-report-btn"
            onClick={onClose}
            className="text-[11px] sm:text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-4 sm:px-5 py-1.5 sm:py-2 rounded-xl transition shadow-md shadow-indigo-600/20 shrink-0 min-h-[36px]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
